import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Hero from '@/components/Hero';
import FAQItem from '@/components/FAQItem';
import Testimonials from '@/components/Testimonials';
import FareHarborCalendar from '@/components/FareHarborCalendar';
import { handleFareHarborBooking } from '@/utils/fareHarborUtils';
import { Star, Clock, MapPin, Users } from 'lucide-react';
import StickyBookingButton from '@/components/StickyBookingButton';
import TourStructuredData from '@/components/tours/TourStructuredData';

const BrooklynHeights = () => {
  // Tour features
  const tourHighlights = [
    {
      title: "Walt Whitman Park",
      description: "Introduction to the tour and its historical context."
    },
    {
      title: "Brooklyn Bridge",
      description: "Discover the story of Ned, the first cat to cross the Brooklyn Bridge in 1883."
    },
    {
      title: "Brooklyn Borough Hall",
      description: "Hear about the heroic cat that made history here."
    },
    {
      title: "Joralemon Street",
      description: "Discover the kitten that brought good luck to the men building the subway tunnel."
    },
    {
      title: "Remsen Street",
      description: "Hear about Harry, the firefighter cat of Remsen Street."
    },
    {
      title: "Brooklyn Heights Promenade",
      description: "Enjoy stunning views and learn about the cat that inspired a community to take action."
    },
    {
      title: "St. George Hotel Area",
      description: "Learn about Minnie, the feline mascot, and the historic cat shows."
    },
    {
      title: "Plymouth Church",
      description: "Hear about the story of Henry Ward Beecher and his beloved \"Hoosier Cat.\""
    },
    {
      title: "Brooklyn Borough Hall",
      description: "We'll end at Brooklyn Borough Hall. From there, it's just a short walk to the famous Brooklyn Cat Cafe if you wish to visit on your own. (NOTE: The cafe is not part of our tour. If you wish to visit, you'll need to make a separate reservation. We are not affiliated with the cafe but are happy to provide their contact information.)"
    }
  ];
  
  // Updated testimonials with Google reviews
  const testimonials = [
    {
      quote: "What a purrfect experience! Peggy led us around the beautiful, historic streets of Brooklyn Heights, telling us fascinating, mind-blowing, and adorable tales of cat's past, as well as their relationships with the New Yorkers of yesteryear.",
      author: "Marie C.",
      role: "History enthusiast"
    },
    {
      quote: "I loved this tour so much! Peggy is wonderful, and so many of the stops along the tour highlight the history of New York, Brooklyn, and Brooklyn Heights alongside the great feline tales. It was a joy to spend the afternoon on this lovely tour.",
      author: "Kate K.",
      role: "Brooklyn Heights visitor"
    },
    {
      quote: "So glad I booked this! Such a cool unique thing to do in BK, me and my friends had a great time. Our guide, Peggy, was entertaining and informative. 10/10, highly recommend.",
      author: "Raquel S.",
      role: "Friend group visitor"
    }
  ];
  
  const faqs = [
    {
      question: "What should I bring on the tour?",
      answer: "Comfortable walking shoes, a water bottle, and an umbrella if rain is expected. Don't forget your camera to capture the charming sights and stories of our cat-filled adventure. If you're able to, bring your cat! In a carrier or a backpack. What ever you're comfortable with! Most importantly, bring a curious spirit and love of cats."
    },
    {
      question: "Is the tour suitable for children?",
      answer: "Yes, the tour is family-friendly and suitable for pre-teens and teenagers who will appreciate hearing about the adventurous cats of New York City. However, please note that the tour involves walking for about 2 hours, so ensure everyone is up for the journey."
    },
    {
      question: "What happens if it rains?",
      answer: "The tour will proceed in light rain, so please bring an umbrella or a raincoat. In case of severe weather, we will reschedule or refund your booking to ensure everyone's safety and comfort. We'll notify you via email in advance if the tour is canceled due to bad weather."
    },
    {
      question: "What kind of cats will we learn about?",
      answer: "Our tour features historical cats from the 1800s and early 1900s who made their mark on Brooklyn Heights. You'll hear stories of working cats, famous feline residents, and the cats who witnessed the neighborhood's development. While our focus is on historical tales rather than modern cat sightings, we occasionally spot neighborhood cats along our route (though we can't guarantee it!)."
    },
    {
      question: "Do I need to book in advance?",
      answer: "Yes, we recommend booking in advance to secure your spot, as tours can fill up quickly. You can easily book your tour through our website."
    },
    {
      question: "Can I take photos during the tour?",
      answer: "Absolutely! Photos are encouraged, but please be respectful and do not use flash photography around the cats. However, we ask that you refrain from taking videos during the tour to ensure the experience is enjoyable for everyone."
    },
    {
      question: "Is there a place to rest during the tour?",
      answer: "Yes, we have a few designated spots where you can sit and rest while enjoying the stories. The cat café at the end of the tour is also a great place to relax and enjoy some refreshments."
    }
  ];

  // FareHarbor booking link and item ID for Brooklyn Heights
  const fareHarborItemId = 562630;

  return (
    <div className="min-h-screen pt-20">
      <Helmet>
        <title>Brooklyn Heights Historical Cat Tour | Cats About Town</title>
        <meta name="description" content="Explore Brooklyn Heights' charming streets and discover fascinating feline history with our unique cat-themed walking tour. Book now!" />
        <meta name="keywords" content="Brooklyn Heights cat tour, NYC cat history, feline walking tour, cat history New York" />
        <link rel="canonical" href="https://catsabouttowntours.com/tours/brooklyn-heights" />
        {/* Breadcrumb structured data for SEO */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://catsabouttowntours.com/" },
              { "@type": "ListItem", "position": 2, "name": "Tours", "item": "https://catsabouttowntours.com/tours" },
              { "@type": "ListItem", "position": 3, "name": "Brooklyn Heights Cat Tour", "item": "https://catsabouttowntours.com/tours/brooklyn-heights" }
            ]
          })}
        </script>
      </Helmet>
      <TourStructuredData tour={{
        name: 'Brooklyn Heights Cat Tour',
        description: "Explore Brooklyn Heights' charming streets and discover fascinating feline history with our unique cat-themed walking tour.",
        image: '/lovable-uploads/0a57130d-68ab-4a75-b052-cfeffcf79b78.png',
        url: 'https://catsabouttowntours.com/tours/brooklyn-heights',
        price: '40',
        location: 'Brooklyn Heights'
      }} />
      
      {/* Hero Section */}
      <Hero 
        title="Brooklyn Heights Historical Cat Tour" 
        subtitle="Stroll through America's first suburb and discover the tales of its historical feline legends"
        backgroundImage="/lovable-uploads/0a57130d-68ab-4a75-b052-cfeffcf79b78.png"
        height="medium"
        buttonText="Check Availability & Book"
        buttonLink="#book-now"
        showUrgencyMessage={true}
        backgroundAlt="View of Brooklyn Heights cat tour"
      />
      
      {/* Quick Info Bar */}
      <section className="bg-[#F5EDE0] py-4 border-b border-[#C58B65]/20">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center items-center gap-x-10 gap-y-3 text-[#3E2723]">
            <div className="flex items-center">
              <Clock size={18} className="mr-2 text-[#C58B65]" />
              <span className="text-sm md:text-base">2 hours</span>
            </div>
            <div className="flex items-center">
              <MapPin size={18} className="mr-2 text-[#C58B65]" />
              <span className="text-sm md:text-base">1.5 miles of walking</span>
            </div>
            <div className="flex items-center">
              <Users size={18} className="mr-2 text-[#C58B65]" />
              <span className="text-sm md:text-base">Small groups (max 12 people)</span>
            </div>
            <div className="flex items-center">
              <Star size={18} className="mr-2 text-[#D4AF37]" />
              <span className="text-sm md:text-base">98% 5-star reviews</span>
            </div>
          </div>
        </div>
      </section>
      
      {/* Tour Overview - Simplified and Focused */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
              <div className="md:col-span-2">
                <h2 className="font-playfair font-bold text-2xl md:text-3xl mb-6 text-[#3E2723]">
                  Discover Brooklyn Heights' Feline History
                </h2>
                <div className="prose prose-lg text-[#3E2723]/90 font-montserrat">
                  <p className="mb-4">
                    Brooklyn Heights is known for its charming streets and stunning skyline views—but behind those brownstones are stories of brave, quirky, and unforgettable cats. This 1.5-mile walking tour dives into the historical feline folklore of the neighborhood, weaving together tales from the 1800s and early 1900s—featuring rescue cats, subway kittens, and a firehouse mascot who became a local hero.
                  </p>
                  <p className="mb-4">
                    As we stroll along the Brooklyn Bridge and gaze across the East River, you'll hear how Ned became the first cat to cross the bridge in 1883 and how other four-legged residents became part of the borough's history. Our tour ends near Brooklyn Borough Hall. If you'd like to visit the Brooklyn Cat Café afterward, it's just a short walk away, though you'll need to make a separate reservation as it's not included in our tour.
                  </p>
                  <p className="mb-4">
                    Join us for a unique exploration of America's first suburb, where every street has a historical cat tale to tell. While we can't guarantee seeing any modern-day felines along our route, the stories of these legendary cats will bring history to life in ways you've never imagined.
                  </p>
                  
                  <div className="bg-[#FAF2E8] p-4 rounded-lg border border-[#C58B65]/20 mb-4">
                    <p className="text-[#8B4513] font-medium">
                      <strong>Tour Note:</strong> Our tours focus on historical stories and are not cat-spotting safaris (though if we see a feline friend along the way, we'll consider it a purr-fect bonus!).
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Sticky Booking Card */}
              <div className="md:sticky md:top-24">
                <div className="bg-[#FAF2E8] p-6 rounded-lg border border-[#C58B65]/20 shadow-md">
                  <h3 className="font-playfair font-bold text-xl mb-4 text-[#3E2723] text-center">
                    Book Your Spot
                  </h3>
                  <p className="text-center mb-4 text-[#3E2723]/80">
                    $40 per person
                  </p>
                  <div className="flex justify-center mb-4">
                    <div className="flex items-center">
                      <Star size={14} className="text-[#D4AF37]" aria-hidden="true" />
                      <Star size={14} className="text-[#D4AF37]" aria-hidden="true" />
                      <Star size={14} className="text-[#D4AF37]" aria-hidden="true" />
                      <Star size={14} className="text-[#D4AF37]" aria-hidden="true" />
                      <Star size={14} className="text-[#D4AF37]" aria-hidden="true" />
                      <span className="ml-2 text-sm text-[#3E2723]/80">
                        (98% 5-star reviews)
                      </span>
                    </div>
                  </div>
                  <a 
                    href="#book-now"
                    className="bg-[#D32F2F] text-white py-3 px-4 rounded font-montserrat font-semibold text-center block hover:bg-[#C62828] transition-colors"
                  >
                    Check Available Dates
                  </a>
                  <p className="text-xs text-center mt-4 text-[#3E2723]/60">
                    Secure booking · Instant confirmation
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Tour Highlights - Visual Grid */}
      <section className="py-12 bg-[#FAF2E8]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-playfair font-bold text-2xl md:text-3xl mb-8 text-[#3E2723] text-center">
              What You'll Discover
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {tourHighlights.map((highlight, index) => (
                <div 
                  key={index} 
                  className="bg-white p-6 rounded-lg shadow-sm border border-[#C58B65]/10 hover:shadow-md transition-shadow"
                >
                  <h3 className="font-playfair font-bold text-lg mb-2 text-[#3E2723]">{highlight.title}</h3>
                  <p className="text-[#3E2723]/80 font-montserrat">{highlight.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      
      {/* Testimonials Section */}
      <Testimonials testimonials={testimonials} />
      
      {/* Booking Calendar Section */}
      <section id="book-now" className="py-12 bg-white scroll-mt-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-playfair font-bold text-2xl md:text-3xl mb-4 text-[#3E2723] text-center">
              Book Your Brooklyn Heights Historical Cat Tour
            </h2>
            <p className="text-center max-w-2xl mx-auto mb-8 text-[#3E2723]/80 font-montserrat">
              Experience the historical feline tales of Brooklyn Heights—a perfect blend of NYC history and captivating cat stories!
            </p>
            
            <FareHarborCalendar itemId={fareHarborItemId} className="mb-6" />
            
            <p className="text-center text-sm text-[#3E2723]/60 font-montserrat">
              Need help booking? <Link to="/contact" className="text-[#D32F2F] hover:underline">Contact us</Link>
            </p>
          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="py-12 bg-[#F5EDE0]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-playfair font-bold text-2xl md:text-3xl mb-8 text-[#3E2723] text-center">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <FAQItem 
                  key={index}
                  question={faq.question}
                  answer={faq.answer}
                />
              ))}
            </div>
            
            <div className="text-center mt-10">
              <Link to="/contact" className="text-[#D32F2F] hover:underline font-montserrat">
                Have another question? Contact us
              </Link>
            </div>
          </div>
        </div>
      </section>
      
      {/* Other Tours */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="font-playfair font-bold text-2xl md:text-3xl mb-8 text-[#3E2723] text-center">
            Explore Our Other Tours
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <div className="bg-[#FAF2E8] p-6 rounded-lg border border-[#C58B65]/10 shadow-sm hover:shadow-md transition-all">
              <h3 className="font-playfair font-bold text-xl mb-3 text-[#3E2723]">Financial District</h3>
              <p className="mb-4 text-[#3E2723]/80 font-montserrat">
                Discover the feline heroes of Wall Street and beyond, from postal cats to City Hall's famous feline residents.
              </p>
              <Link to="/tours/financial-district" className="text-[#D32F2F] hover:underline font-montserrat block text-center">
                View Tour
              </Link>
            </div>
            
            <div className="bg-[#FAF2E8] p-6 rounded-lg border border-[#C58B65]/10 shadow-sm hover:shadow-md transition-all">
              <h3 className="font-playfair font-bold text-xl mb-3 text-[#3E2723]">Lower East Side</h3>
              <p className="mb-4 text-[#3E2723]/80 font-montserrat">
                Dive into the gritty past of the Lower East Side, where tenacious cats lived alongside immigrants and artists.
              </p>
              <Link to="/tours/lower-east-side" className="text-[#D32F2F] hover:underline font-montserrat block text-center">
                View Tour
              </Link>
            </div>
            
            <div className="bg-[#FAF2E8] p-6 rounded-lg border border-[#C58B65]/10 shadow-sm hover:shadow-md transition-all">
              <h3 className="font-playfair font-bold text-xl mb-3 text-[#3E2723]">Private Tours</h3>
              <p className="mb-4 text-[#3E2723]/80 font-montserrat">
                Create a customized cat history experience for your group, tailored to your interests and schedule.
              </p>
              <Link to="/tours/private-tours" className="text-[#D32F2F] hover:underline font-montserrat block text-center">
                View Options
              </Link>
            </div>
          </div>
        </div>
      </section>
      
      {/* Mobile sticky booking button */}
      <StickyBookingButton link="#book-now" />
    </div>
  );
};

export default BrooklynHeights;
